import { useAssignTable } from '@shared/components/assign-table';
import { VehicleAssignTableDataDto } from '@interfaces/models';
import { useVehicles } from '@pages/employees/hooks';
import { getAssignVehicles } from '@api/vehicles';
import { useState, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PaginationState } from '@ui/components';
import { getTableRequest } from '@shared/utils';
import { useTranslation } from 'react-i18next';
import { useDebouncedState } from '@ui/hooks';

import { getVehiclesColumns } from './employee-assign-vehicles-table-columns';

interface VehiclesTableState {
  pagination: PaginationState;
  globalFilter: string;
}

const vehiclesTableInitialState: VehiclesTableState = {
  pagination: {
    pageIndex: 0,
    pageSize: 4,
  },
  globalFilter: '',
};

export const useEmployeeAssignVehicles = (assignedVehicles: string[]) => {
  const { t } = useTranslation();
  const columns = useMemo(() => getVehiclesColumns(t), [t]);
  const [vehicleSearch, setVehicleSearch] = useDebouncedState<string>('', 800);
  const [state, setState] = useState<VehiclesTableState>(vehiclesTableInitialState);
  const { vehiclesOptions, isVehiclesFetching } = useVehicles(vehicleSearch);
  const totalItemsRef = useRef(0);

  const request = useMemo(() => {
    return getTableRequest({
      pagination: state.pagination,
      search: state.globalFilter,
    });
  }, [state]);

  const {
    data: selectedVehicles,
    isLoading: isSelectedVehiclesLoading,
    isFetching: isSelectedVehiclesFetching,
  } = useQuery({
    queryKey: ['employee-vehicles-table', request, assignedVehicles],
    queryFn: () => getAssignVehicles({ ...request, ids: assignedVehicles }),
  });

  const totalItems = useMemo(() => {
    if (!isSelectedVehiclesFetching) {
      totalItemsRef.current = selectedVehicles?.pagination.totalItems ?? 0;
    }
    return totalItemsRef.current;
  }, [selectedVehicles, isSelectedVehiclesFetching]);

  const assignTable = useAssignTable<VehicleAssignTableDataDto>({
    isLoading: isSelectedVehiclesLoading && isSelectedVehiclesFetching,
    data: selectedVehicles?.data ?? [],
    phraseChange: setVehicleSearch,
    getRowId: (row) => row.id,
    options: vehiclesOptions,
    onStateChange: (state) => {
      setState(state as VehiclesTableState);
    },
    isOptionLoading: isVehiclesFetching,
    initialState: state,
    totalItems,
    columns,
  });

  return assignTable;
};
